import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDisplayDate } from '../../../utils/dateFormat';
import { hasPermission } from '../../../utils/permissions';

const INSURANCE_HELP_TEXT =
  'This snapshot reflects the open (current) coverage period on the referral. Intake often sets private insurance, company, and plan type; ' +
  'waivers and exemption are usually maintained afterward by the service coordinator. Use “View list” to see each waiver here. ' +
  'Use Edit insurance (or View insurance) to open the dedicated page for coverage, waivers, and exemption without the full referral form—you can also reach the same workspace from an open referral.';

/** SVG info mark — aligns cleanly next to the section title */
function ClientInsuranceInfoMark() {
  return (
    <svg
      className="client-insurance-summary-info-svg"
      viewBox="0 0 20 20"
      width="20"
      height="20"
      aria-hidden
      focusable="false"
    >
      <circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" strokeWidth="1.35" />
      <circle cx="10" cy="7" r="1.15" fill="currentColor" />
      <rect x="9" y="9.6" width="2" height="5.2" rx="1" fill="currentColor" />
    </svg>
  );
}

/**
 * Read-only snapshot of current referral insurance (8a). Edit opens slim client → referral insurance route.
 */
function ClientInsuranceSummarySection({ clientId, tenantPath, summaryPayload }) {
  const navigate = useNavigate();
  const p = tenantPath || ((path) => path);
  const canReadReferral = hasPermission('referrals.read');
  const canUpdateReferral = hasPermission('referrals.update');
  const showInsuranceWorkspaceLink = canReadReferral || canUpdateReferral;
  const referralId = summaryPayload?.referral_id;
  const s = summaryPayload?.summary;
  const [insuranceHelpOpen, setInsuranceHelpOpen] = useState(false);
  const helpId = 'client-insurance-summary-help';

  if (!referralId || !s) {
    return null;
  }

  const waiverList = Array.isArray(s.waivers) ? s.waivers : [];

  return (
    <div className="form-section client-insurance-summary-section">
      <div className="section-title-row">
        <div className="client-insurance-summary-title-block">
          <div className="client-insurance-summary-title-inline">
            <h3 className="section-title client-insurance-summary-heading">
              Insurance{' '}
              <span className="client-insurance-summary-period">(current period)</span>
            </h3>
            <button
              type="button"
              className="client-insurance-summary-info-btn"
              onClick={() => setInsuranceHelpOpen((v) => !v)}
              aria-expanded={insuranceHelpOpen}
              aria-controls={helpId}
              aria-label={insuranceHelpOpen ? 'Hide insurance help' : 'Show insurance help'}
            >
              <ClientInsuranceInfoMark />
            </button>
          </div>
          {insuranceHelpOpen ? (
            <p id={helpId} className="client-insurance-summary-help-text">
              {INSURANCE_HELP_TEXT}
            </p>
          ) : null}
        </div>
        {showInsuranceWorkspaceLink && (
          <button
            type="button"
            className="btn-view-referral"
            onClick={() => navigate(p(`/client/${clientId}/referral/${referralId}/insurance`))}
          >
            {canUpdateReferral ? 'Edit insurance' : 'View insurance'}
          </button>
        )}
      </div>
      <div className="form-grid client-insurance-summary-grid">
        <div className="form-group">
          <label>Private insurance</label>
          <div className="readonly-field">{s.insurance_posture_label || s.insurance_posture}</div>
        </div>
        <div className="form-group">
          <label>Insurance company</label>
          <div className="readonly-field">{s.insurance_company_name || '—'}</div>
        </div>
        <div className="form-group">
          <label>Plan Type</label>
          <div className="readonly-field">{s.insurance_type_description || '—'}</div>
        </div>
        <div className="form-group">
          <label>Coverage start</label>
          <div className="readonly-field">{s.effective_start ? formatDisplayDate(s.effective_start) : '—'}</div>
        </div>
        <div className="form-group client-insurance-summary-waivers">
          <label>Waivers</label>
          <div className="readonly-field">
            {typeof s.waiver_count === 'number' ? s.waiver_count : '—'}
            {waiverList.length > 0 ? (
              <details className="client-insurance-waiver-expand">
                <summary>View list</summary>
                <ul className="client-insurance-waiver-list">
                  {waiverList.map((w, idx) => (
                    <li key={idx}>
                      <div>
                        <strong>{w.provider_line || '—'}</strong>
                        {w.site_line ? <span> · {w.site_line}</span> : null}
                      </div>
                      <div className="client-insurance-waiver-dates">
                        {w.start_date ? formatDisplayDate(w.start_date) : '—'}
                        {' — '}
                        {w.end_date ? formatDisplayDate(w.end_date) : 'open'}
                        {w.note ? ` · ${w.note}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            ) : null}
          </div>
        </div>
        <div className="form-group client-insurance-summary-exemption">
          <label>Exemption</label>
          <div className="readonly-field">
            {s.exemption
              ? `${formatDisplayDate(s.exemption.start_date)} — ${s.exemption.end_date ? formatDisplayDate(s.exemption.end_date) : 'open'}`
              : 'None'}
          </div>
        </div>
        {s.note ? (
          <div className="form-group form-group-full client-insurance-summary-coverage-note">
            <label>Coverage note</label>
            <div className="readonly-field">{s.note}</div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default ClientInsuranceSummarySection;
